// One brand-direction gallery card (Flow 3; docs/phase-0/slice5-component-designs.md
// §3.3 `.direction-card`) - a tap-to-preview card whose ONLY commit action is the
// explicit "Choose this direction" button (never an accidental commit from a hover or
// a single stray tap, ux spec §6.1). The role gate (viewer) disables the button and
// carries the reason in a `.tooltip-wrap` bubble, per the shared role-gating visual
// (component-designs.md §2.4) - never a hidden control.
import { useId } from "react";
import type { BrandDirection } from "@nightcrew/shared";
import { mediaAssetUrl } from "../api/brand.js";
import { buildCopy } from "../copy/build.js";

export const BrandDirectionCard = ({
  direction,
  selected,
  disabledReason,
  onChoose,
}: {
  readonly direction: BrandDirection;
  readonly selected: boolean;
  /** Present only for a viewer (role-gated, never actionable); omitted entirely for an
   * owner/admin so the button is never rendered disabled-with-no-reason. */
  readonly disabledReason?: string;
  readonly onChoose: () => void;
}): React.JSX.Element => {
  const reasonId = useId();
  const disabled = disabledReason !== undefined;

  return (
    <div className="card pad direction-card" data-testid="direction-card" aria-pressed={selected}>
      <div className="direction-swatch-strip row gap8" aria-hidden="true">
        {direction.palette.map((color, index) => (
          <span
            key={`${color}-${String(index)}`}
            className="direction-swatch"
            style={{
              width: 20,
              height: 20,
              borderRadius: 4,
              background: color,
              border: "1px solid var(--line)",
              display: "inline-block",
            }}
          />
        ))}
      </div>
      <h3 className="h3 mt12">{direction.name}</h3>
      <p className="muted" style={{ fontSize: 13 }}>
        {direction.type}
      </p>
      {direction.logoRefs[0] !== undefined ? (
        <img
          className="direction-logo-concept mt8"
          src={mediaAssetUrl(direction.logoRefs[0])}
          alt=""
          width={64}
          height={64}
          style={{ objectFit: "contain", border: "1px solid var(--line)", borderRadius: "var(--r-sm)" }}
          onError={(event) => {
            event.currentTarget.style.visibility = "hidden";
          }}
        />
      ) : null}
      <div className="tooltip-wrap mt16">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={disabled}
          aria-describedby={disabled ? reasonId : undefined}
          onClick={onChoose}
        >
          {buildCopy("build.brand.directions.chooseCta")}
        </button>
        {disabled ? (
          <span className="tooltip-bubble" id={reasonId} role="tooltip">
            {disabledReason}
          </span>
        ) : null}
      </div>
    </div>
  );
};
